import { CalculationParams } from '@features/settings/reducer';
import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import { FormattedMessage } from 'react-intl';

import Brake from '../assets/brake.png';
import Coasting from '../assets/coasting.png';
import CruiseControl from '../assets/cruise-control.png';
import AccelerationPedal from '../assets/kickdown.png';
import Recuperation from '../assets/refresh.png';
import Overspeed from '../assets/speed.png';

const styles = StyleSheet.create({
    calculationRatingsWrap: {
        position: 'absolute',
        bottom: 60,
        left: 20,
        right: 20,
    },

    calculationRatings: {
        flexDirection: 'column',
        width: '100%',
        backgroundColor: '#E5EBF0',
        paddingVertical: 10,
        paddingHorizontal: 12,
    },

    noteRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        marginBottom: 8,
    },

    noteLabel: {
        fontSize: 10,
        fontFamily: 'Bold',
        marginRight: 4,
    },

    noteText: {
        fontSize: 10,
    },

    iconsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
    },

    groupRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: 12,
    },

    groupLabel: {
        fontSize: 10,
        fontFamily: 'Bold',
        marginRight: 6,
    },

    itemWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 4,
    },

    weightItem: {
        flexDirection: 'row',
        alignItems: 'center',
    },

    icon: {
        width: 12,
        height: 12,
        marginRight: 4,
    },

    weightTitle: {
        fontSize: 10,
        fontFamily: 'Bold',
    },

    weightValue: {
        fontFamily: 'Bold',
        fontSize: 10,
    },

    separator: {
        fontSize: 12,
        marginLeft: 4,
        marginRight: 4,
        color: '#9AA3AF',
    },

    overspeedRow: {
        alignItems: 'center',
        backgroundColor: '#F6F8F9',
        paddingVertical: 3.5,
    },

    overspeedText: {
        fontSize: 10,
    },

    overspeedWarning: {
        flexDirection: 'row',
        alignItems: 'center',
    },

    exclamationSign: {
        width: 10,
        height: 10,
        marginRight: 4,
    },
});

const checkWeight = (weight: number) => {
    if (weight === 0) return <FormattedMessage id="calculationParams.off" />;
    return `${weight}%`;
};

export default function CalculationParameters({
    calculationParams,
    isOverspeedThresholdChanged,
    hasElectric = false,
    hasDiesel = false,
    isUpdated = false,
}: {
    calculationParams: CalculationParams;
    isOverspeedThresholdChanged: boolean;
    hasElectric?: boolean;
    hasDiesel?: boolean;
    isUpdated?: boolean;
}) {
    const electricIconItems = [
        {
            key: 'acceleration',
            icon: AccelerationPedal,
            label: <FormattedMessage id="discipline.accelerationPedal" />,
            value: calculationParams.electricAccelerationPedalRating.weight,
        },
        {
            key: 'cruise',
            icon: CruiseControl,
            label: <FormattedMessage id="discipline.cruiseControl" />,
            value: calculationParams.electricCruiseControlRating.weight,
        },
        {
            key: 'overspeed',
            icon: Overspeed,
            label: <FormattedMessage id="discipline.overspeed" />,
            value: calculationParams.electricOverSpeedRating.weight,
        },
        {
            key: 'recuperation',
            icon: Recuperation,
            label: <FormattedMessage id="discipline.recuperation" />,
            value: calculationParams.recuperationRating.weight,
        },
    ];

    const dieselIconItems = [
        {
            key: 'coasting',
            icon: Coasting,
            label: <FormattedMessage id="discipline.coasting" />,
            value: calculationParams.coastingRating.weight,
        },
        {
            key: 'acceleration',
            icon: AccelerationPedal,
            label: <FormattedMessage id="discipline.accelerationPedal" />,
            value: calculationParams.accelerationPedalRating.weight,
        },
        {
            key: 'braking',
            icon: Brake,
            label: <FormattedMessage id="discipline.braking" />,
            value: calculationParams.brakingRating.weight,
        },
        {
            key: 'cruise',
            icon: CruiseControl,
            label: <FormattedMessage id="discipline.cruiseControl" />,
            value: calculationParams.cruiseControlRating.weight,
        },
        {
            key: 'overspeed',
            icon: Overspeed,
            label: <FormattedMessage id="discipline.overspeed" />,
            value: calculationParams.overSpeedRating.weight,
        },
    ];

    const renderItems = (items: typeof dieselIconItems) =>
        items.map((item, index) => (
            <View key={item.key} style={styles.itemWrapper}>
                <View style={styles.weightItem}>
                    <View style={styles.icon}>
                        <Image src={item.icon} />
                    </View>
                    <Text style={styles.weightTitle}>
                        {item.label}:{' '}
                        <Text style={styles.weightValue}>{checkWeight(item.value)}</Text>
                    </Text>
                </View>

                {index < items.length - 1 && <Text style={styles.separator}>|</Text>}
            </View>
        ));

    const showMixed = hasDiesel && hasElectric;

    return (
        <View style={styles.calculationRatingsWrap}>
            <View fixed style={styles.calculationRatings}>
                <View style={styles.noteRow}>
                    <Text style={styles.noteLabel}>
                        <FormattedMessage id="calculationParams.note.label" />
                    </Text>
                    <Text style={styles.noteText}>
                        {isUpdated ? (
                            <FormattedMessage id="calculationParams.note.newDescription" />
                        ) : (
                            <FormattedMessage id="calculationParams.note.description" />
                        )}
                    </Text>
                </View>

                {showMixed ? (
                    <>
                        <View style={styles.itemWrapper}>
                            <Text style={styles.groupLabel}>
                                <FormattedMessage id="vehicleFuelType.fuel" />:
                            </Text>
                            <View style={styles.iconsRow}>{renderItems(dieselIconItems)}</View>
                        </View>
                        <View style={styles.itemWrapper}>
                            <Text style={styles.groupLabel}>
                                <FormattedMessage id="vehicleFuelType.electric" />:
                            </Text>
                            <View style={styles.iconsRow}>{renderItems(electricIconItems)}</View>
                        </View>
                    </>
                ) : (
                    <View style={styles.iconsRow}>
                        {hasElectric
                            ? renderItems(electricIconItems)
                            : renderItems(dieselIconItems)}
                    </View>
                )}
            </View>

            {isOverspeedThresholdChanged && (
                <View style={styles.overspeedRow}>
                    <View style={styles.overspeedWarning}>
                        <Text style={styles.overspeedText}>
                            <FormattedMessage id="multipleOverspeedThresholds.note.message" />
                        </Text>
                    </View>
                </View>
            )}
        </View>
    );
}
